import { NgClass } from "@angular/common"
import { Component, inject, OnInit, ViewChild, ViewEncapsulation } from "@angular/core"
import { FormArray, ReactiveFormsModule, UntypedFormControl } from "@angular/forms"
import { CharityOption, CharityService, InterviewService, InterviewState, Question } from "@bequeathed/ngx-services"
import { LoggerService } from "@bequeathed/ngx-app-core"
import { FormControlPipe } from "@bequeathed/ngx-utils"
import { ErrorInfo } from "../../base-form/base-form.component"
import { BaseControl } from "../base-control/base-control.component"
import { CheckboxControl } from "../checkbox-control/checkbox-control.component"
import { RadioButtonControl } from "../radio-button-control/radio-button-control.component"

const log = LoggerService.get("CharityControl")

/**
 * Displays a charity with details
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: "charity-control",
    templateUrl: "./charity-control.component.html",
    styleUrls: [
        "./charity-control.component.sass",
        "../checkbox-control/checkbox-control.component.sass",
        "../../base-form/base-form.component.sass",
        "../base-input-control/base-input-control.component.sass",
    ],
    standalone: true,
    imports: [
        // Angular
        NgClass,
        ReactiveFormsModule,
        // Bequeathed
        FormControlPipe,
        RadioButtonControl,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CharityControl extends CheckboxControl implements OnInit {

    charityService = inject(CharityService)
    interviewService = inject(InterviewService)

    initiatorCharity: CharityOption | undefined
    availableCharityOptions: CharityOption[] = []
    allCharityOptions: CharityOption[] = []

    charityOptionsList: CharityOption[] = []
    selectedCharities: string[] = []
    addAnother: Question | undefined
    @ViewChild("addAnotherControl") addAnotherControl: BaseControl | undefined

    constructor() {
        super()
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit()
        this.addAnother = undefined

        await this.retrieveCharities()
        await this.organiseCharityList()
        if (this.editingResponse) {
            this.applyCharitySelection()
        }
        this.populateCharityControl()
    }

    private async retrieveCharities() {
        if (this.allCharityOptions.length === 0 || this.question.charityType !== "INITIATOR_ONLY") {
            log.debug("retrieveCharities", "Retrieving available charity options")

            this.allCharityOptions = await this.interviewService.getAvailableCharityOptions(
                this.baseForm.interviewState()!.interviewId!,
                this.baseForm.interviewState()!.currentQuestion!.id!,
            )
        }
    }

    private async organiseCharityList() {
        const initiator = this.baseForm.interviewState()?.metadata?.charityInitiator
        if (initiator) {
            if (!this.initiatorCharity) {
                this.initiatorCharity =
                    this.allCharityOptions.find(c => c.initiator === initiator) ||
                    await this.charityService.getCharityByInitiator(initiator) ||
                    undefined
            }
            if (this.question.charityType === "INITIATOR_ONLY") {
                this.charityOptionsList = [this.initiatorCharity!]
            } else if (this.question.charityType === "INITIATOR_FIRST" && !!this.baseForm.interviewState()?.metadata?.charityInitiator) {
                this.charityOptionsList = [this.initiatorCharity!,
                    ...this.allCharityOptions.filter(c => c.charityId != this.initiatorCharity?.charityId)]
            } else if (this.question.charityType === "PREFERRED_WITHOUT_INITIATOR" && !!this.initiatorCharity) {
                this.charityOptionsList = this.allCharityOptions.filter(c => c.charityId != this.initiatorCharity?.charityId)
            } else {
                this.charityOptionsList = this.allCharityOptions
            }
        } else {
            this.charityOptionsList = this.allCharityOptions
        }
    }

    private applyCharitySelection() {
        log.debug("applyCharitySelection", "Currently editing, applying previous answer")
        this.selectedCharities = this.question.userResponse != null ? this.question.userResponse!.split("|") : this.question.calculatedResponse!.split("|")
    }

    private populateCharityControl() {
        log.debug("populateCharityControl", "Populating available charity list")
        this.charityOptionsList.forEach((charity) => {
            // this is how the response looks
            const charityFull = `${charity.accountShortName} - ${charity.charityMissionStatement}`
            const control: UntypedFormControl = new UntypedFormControl(this.selectedCharities.indexOf(charityFull) > -1)

            // @ts-expect-error this is fine
            control["id"] = charity.charityId;
            (this.control as FormArray).controls.push(control)
        })
    }

    toggleCharity(charity: CharityOption) {
        let removedCharity = false
        for (let i = 0; i < this.selectedCharities.length; i++) {
            if (this.selectedCharities[i] === charity.accountShortName) {
                this.selectedCharities.splice(i, 1)
                removedCharity = true
            }
        }
        if (!removedCharity) {
                this.selectedCharities.push(charity.accountShortName!)
            }

    }

    override async validate(): Promise<ErrorInfo[] | undefined> {
        let errors = await super.validate()
        if (!errors?.length) {

            const values: string[] = [];
            (this.control as FormArray).controls.filter((ctrl: any) => !!ctrl.value).forEach((ctrl: any) => values.push(ctrl.id))

            if (!values.length && !this.addAnotherControl!.control.value?.match(/(.*##[Yy])es/g)) {
                errors = [{
                    field: this.questionId,
                    error: "equal_charity_selection_or_add_another_required",
                } as ErrorInfo]
                this.control.setErrors([{invalid: true}])
                this.control?.markAllAsTouched()
                this.control?.updateValueAndValidity()
            }
        }
        return errors
    }

    override applyValue(state: InterviewState) {
        // Resolve the effective charity selection. When editing from the summary, the
        // charity may already be answered (e.g. an INITIATOR_ONLY question pre-selected
        // to "Blue Cross") and the user only touches the "add another" question without
        // re-ticking the charity. In that case `selectedCharities` is empty, so fall back
        // to the previously stored answer (userResponse, then calculatedResponse) rather
        // than wiping the required `*.site.names` source question.
        const resolvedCharities = this.selectedCharities.length > 0
            ? this.selectedCharities
            : (state.currentQuestion!.userResponse || state.currentQuestion!.calculatedResponse || "")
                .split("|")
                .filter((charity: string) => !!charity)

        state.currentQuestion!.userResponse = resolvedCharities.join("|")

        this.addAnotherControl?.applyValue(this.addAnother!)

        if (resolvedCharities.length > 0) {
            state.currentQuestion!.groupedQuestions = new Map<number, Question[]>()
            resolvedCharities.forEach((charity: string, index: number) => {
                const question = state.currentQuestion?.questions?.find((q: Question) => q.id?.endsWith("name"))
                if (!question) return

                state.currentQuestion!.groupedQuestions!.set(index, [new Question({
                    id: `${question?.sourceId}[${index}].${question.id}`,
                    type: "DISPLAY",
                    calculatedResponse: charity,
                    userResponse: charity
                })])
            })
        }
    }

    findAddAnotherQuestion(): Question | undefined {
        if (this.question.questions?.length && !this.addAnother) {
            this.addAnother = this.question.questions.find((q: Question) => q.id!.endsWith("add_another"))
        }
        return this.addAnother
    }
}

